import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BaseRiskCalculation, Step3Request, ClinicalSessionStep } from '@/dtos/FlowClinicalSessions/index';
import { useProcessStep3 } from '@/hooks/FlowClinical';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, TrendingUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface Step3Props {
  sessionId: string;
  onSuccess: (sessionId: string) => void;
  onBack: () => void;
  onError?: (message: string) => void;
  riskCalculations?: BaseRiskCalculation[];
  currentStep?: ClinicalSessionStep;
}

// Risk level configuration with colors and labels
const RISK_LEVEL_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
  low: { label: 'Risc Scăzut', variant: 'outline', className: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
  medium: { label: 'Risc Mediu', variant: 'default', className: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' },
  high: { label: 'Risc Ridicat', variant: 'default', className: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' },
  veryhigh: { label: 'Risc Foarte Ridicat', variant: 'destructive', className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
};

const RECOMMENDATION_CLASS_BY_LEVEL: Record<string, string> = {
  low: 'bg-white border-border',
  medium: 'bg-white border-border',
  high: 'bg-white border-border',
  veryhigh: 'bg-white border-border',
};

const extractApiErrorMessage = (error: unknown): string => {
  const maybeAxiosError = error as {
    response?: { data?: { errorMessage?: string } };
    message?: string;
  };
  return (
    maybeAxiosError?.response?.data?.errorMessage ||
    maybeAxiosError?.message ||
    'Failed to process step 3'
  );
};

export default function Step3({
  sessionId,
  onSuccess,
  onBack,
  onError,
  riskCalculations: initialRiskCalculations,
  currentStep,
}: Step3Props) {
  const processStep3Mutation = useProcessStep3();
  const [riskCalculations, setRiskCalculations] = useState<BaseRiskCalculation[]>(initialRiskCalculations || []);
  // Check if Step3 is already processed - progress should be Guidance (4) or Completed (5)
  // If currentStep is still Results (3), we need to process Step3
  const isAlreadyProcessed = currentStep === ClinicalSessionStep.Guidance || currentStep === ClinicalSessionStep.Completed;
  const [hasProcessed, setHasProcessed] = useState(isAlreadyProcessed);
  const processingInitiatedRef = useRef(false); // Track if we've already initiated processing
  const failureHandledRef = useRef(false);

  const handleStep3Failure = (message: string) => {
    if (failureHandledRef.current) {
      return;
    }

    failureHandledRef.current = true;
    onError?.(message);
    onBack();
  };

  // Update riskCalculations when prop changes (e.g., after refetch)
  useEffect(() => {
    if (initialRiskCalculations && initialRiskCalculations.length > 0) {
      setRiskCalculations(initialRiskCalculations);
    }
  }, [initialRiskCalculations]);

  // Auto-process Step3 when component mounts if not already processed
  useEffect(() => {
    // Update hasProcessed based on currentStep
    const isProcessed = currentStep === ClinicalSessionStep.Guidance || currentStep === ClinicalSessionStep.Completed;
    if (isProcessed !== hasProcessed) {
      setHasProcessed(isProcessed);
      // Reset processing flag if step is already processed
      if (isProcessed) {
        processingInitiatedRef.current = false;
      }
    }

    // Only process if:
    // 1. Not already processed
    // 2. Not currently processing
    // 3. Haven't already initiated processing
    // 4. Have a valid sessionId
    if (!hasProcessed && !isProcessed && sessionId && !processStep3Mutation.isPending && !processingInitiatedRef.current) {
      console.log('[Step3] Auto-processing Step3 for session:', sessionId, 'currentStep:', currentStep);
      failureHandledRef.current = false;
      processingInitiatedRef.current = true; // Mark as initiated
      const request: Step3Request = {
        sessionId: sessionId,
      };

      processStep3Mutation.mutate(request, {
        onSuccess: (response) => {
          console.log('[Step3] Step3 response:', response);
          if (response.isSuccess && response.data) {
            console.log('[Step3] Step3 completed successfully. Progress:', response.data.progress);
            setRiskCalculations(response.data.riskCalculations || []);
            setHasProcessed(true);
            failureHandledRef.current = false;
            onSuccess(response.data.sessionId);
          } else {
            console.error('[Step3] Step3 failed:', response.errorMessage);
            handleStep3Failure(response.errorMessage || 'Failed to process step 3');
            processingInitiatedRef.current = false; // Reset on failure
          }
        },
        onError: (error) => {
          console.error('[Step3] Failed to process Step3:', error);
          handleStep3Failure(extractApiErrorMessage(error));
          processingInitiatedRef.current = false; // Reset on error
        },
      });
    } else {
      console.log('[Step3] Skipping auto-process:', { hasProcessed, isProcessed, currentStep, sessionId, isPending: processStep3Mutation.isPending, processingInitiated: processingInitiatedRef.current });
    }
  }, [sessionId, currentStep, hasProcessed, processStep3Mutation, onSuccess, onError]);

  const handleProcessStep3 = async () => {
    if (!sessionId || processStep3Mutation.isPending || processingInitiatedRef.current) return;

    failureHandledRef.current = false;
    processingInitiatedRef.current = true; // Mark as initiated
    const request: Step3Request = {
      sessionId: sessionId,
    };

    try {
      const response = await processStep3Mutation.mutateAsync(request);
      if (response.isSuccess && response.data) {
        setRiskCalculations(response.data.riskCalculations || []);
        setHasProcessed(true);
        failureHandledRef.current = false;
        onSuccess(response.data.sessionId);
      } else {
        handleStep3Failure(response.errorMessage || 'Failed to process step 3');
        processingInitiatedRef.current = false; // Reset on failure
      }
    } catch (error) {
      // Error is handled by the mutation's onError
      console.error('Failed to process Step3:', error);
      handleStep3Failure(extractApiErrorMessage(error));
      processingInitiatedRef.current = false; // Reset on error
    }
  };

  const getRiskLevelKey = (riskLevel?: string) => {
    if (!riskLevel) return 'low';
    return riskLevel.toLowerCase().replace(/[\s-_]/g, '');
  };

  const getRiskLevelConfig = (riskLevel?: string) => {
    const normalized = getRiskLevelKey(riskLevel);
    return RISK_LEVEL_CONFIG[normalized] || RISK_LEVEL_CONFIG.low;
  };

  const isLoading = processStep3Mutation.isPending && !hasProcessed;

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-bold'>Rezultate Evaluare</h2>
          <p className='text-muted-foreground mt-1'>Scorurile și recomandările calculate pe baza răspunsurilor dvs.</p>
        </div>
      </div>

      {isLoading && (
        <Card>
          <CardContent className='py-12 text-center'>
            <Loader2 className='h-12 w-12 mx-auto mb-4 animate-spin text-primary' />
            <p className='text-lg font-medium'>Se calculează rezultatele...</p>
            <p className='text-sm text-muted-foreground mt-2'>Vă rugăm să așteptați, acest proces poate dura câteva secunde.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && riskCalculations.length === 0 && hasProcessed && (
        <Alert>
          <AlertTriangle className='h-4 w-4' />
          <AlertDescription>Nu s-au găsit rezultate calculate. Vă rugăm să încercați din nou.</AlertDescription>
        </Alert>
      )}

      {!isLoading && riskCalculations.length > 0 && (
        <div className='space-y-6'>
          {riskCalculations.map((riskCalc, index) => {
            const riskLevel = riskCalc.result?.riskLevel;
            const riskLevelKey = getRiskLevelKey(riskLevel);
            const riskConfig = getRiskLevelConfig(riskLevel);
            const summary = riskCalc.result?.summary;
            const recommendations = riskCalc.result?.recommendations || [];
            const explanations = riskCalc.result?.explanations;
            const recommendationClassName =
              RECOMMENDATION_CLASS_BY_LEVEL[riskLevelKey] || 'bg-muted border-border';

            return (
              <Card key={riskCalc.id || index} className='overflow-hidden'>
                <CardHeader className='border-b'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <CardTitle className='flex items-center gap-2'>
                        <TrendingUp className='h-5 w-5' />
                        {summary?.title || `Evaluare ${index + 1}`}
                      </CardTitle>
                      {summary?.description && <CardDescription className='mt-2'>{summary.description}</CardDescription>}
                    </div>
                    <Badge variant={riskConfig.variant} className={`ml-4 ${riskConfig.className}`}>
                      {riskConfig.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className='pt-6 space-y-6'>
                  {/* Summary */}
                  {summary && (
                    <div className='space-y-2'>
                      <h4 className='font-semibold text-lg'>Sumar</h4>
                      <p className='text-muted-foreground'>{summary.description || summary.title}</p>
                      {riskLevel && (
                        <div className='flex items-center gap-2 text-sm'>
                          <span className='text-muted-foreground'>Nivel de risc:</span>
                          <Badge variant={riskConfig.variant} className={riskConfig.className}>
                            {riskConfig.label}
                          </Badge>
                        </div>
                      )}
                      {summary.calculatedAt && (
                        <p className='text-xs text-muted-foreground'>
                          Calculat la: {new Date(summary.calculatedAt).toLocaleString('ro-RO')}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Explanations */}
                  {explanations && (
                    <div className='space-y-3'>
                      <div className='flex items-center gap-2'>
                        <h4 className='font-semibold'>Explicații</h4>
                        {riskLevel && (
                          <Badge variant={riskConfig.variant} className={riskConfig.className}>
                            {riskConfig.label}
                          </Badge>
                        )}
                      </div>
                      {explanations.topContributors && explanations.topContributors.length > 0 && (
                        <div>
                          <p className='text-sm font-medium mb-2'>Factori principali:</p>
                          <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
                            {explanations.topContributors.map((contributor, idx) => (
                              <li key={idx}>
                                <span className='font-medium'>{contributor.factor}:</span> {contributor.impact}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {explanations.triggeredRules && explanations.triggeredRules.length > 0 && (
                        <div>
                          <p className='text-sm font-medium mb-2'>Reguli activate:</p>
                          <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
                            {explanations.triggeredRules.map((rule, idx) => (
                              <li key={idx}>{rule}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recommendations */}
                  {recommendations.length > 0 && (
                    <div className='space-y-3 border-t pt-4'>
                      <h4 className='font-semibold flex items-center gap-2'>
                        <CheckCircle2 className='h-5 w-5 text-primary' />
                        Recomandări
                      </h4>
                      <div className='space-y-3'>
                        {recommendations.map((rec, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-lg border ${recommendationClassName}`}
                          >
                            {rec.code && (
                              <Badge variant='outline' className={`mb-2 ${riskConfig.className}`}>
                                {rec.code}
                              </Badge>
                            )}
                            <p className='text-sm'>{rec.text || 'Recomandare fără text'}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Confidence */}
                  {riskCalc.result?.confidence && (
                    <div className='border-t pt-4'>
                      <p className='text-sm text-muted-foreground'>
                        <span className='font-medium'>Nivel de încredere:</span>{' '}
                        {Math.round((riskCalc.result.confidence.score || 0) * 100)}%
                      </p>
                      {riskCalc.result.confidence.assumptions && riskCalc.result.confidence.assumptions.length > 0 && (
                        <p className='text-xs text-muted-foreground mt-1'>
                          Presupuneri: {riskCalc.result.confidence.assumptions.join(', ')}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Action Buttons */}
      <div className='flex items-center justify-between pt-6'>
        <Button variant='outline' onClick={onBack} disabled={isLoading}>
          <ArrowLeft className='h-4 w-4 mr-2' />
          Înapoi
        </Button>
        {!hasProcessed && !isLoading && (
          <Button onClick={handleProcessStep3} disabled={!sessionId || processStep3Mutation.isPending}>
            {processStep3Mutation.isPending ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Se procesează...
              </>
            ) : (
              <>
                <TrendingUp className='h-4 w-4 mr-2' />
                Calculează Rezultatele
              </>
            )}
          </Button>
        )}
        {hasProcessed && (
          <Button onClick={() => onSuccess(sessionId)}>
            Continuă
            <ArrowRight className='h-4 w-4 ml-2' />
          </Button>
        )}
      </div>
    </div>
  );
}
